import { useEffect, useState } from "react";
import { api, money, slotLabel, formatApiErrorDetail } from "@/api";
import { Link } from "react-router-dom";
import { Video, CalendarClock, Star, X } from "lucide-react";
import { toast } from "sonner";

const badge = (s) => ({
  confirmed: "border-[#00e5ff]/50 text-[#00e5ff]",
  completed: "border-emerald-500/50 text-emerald-400",
  pending_payment: "border-amber-500/50 text-amber-400",
  cancelled: "border-[#ff3b30]/50 text-[#ff3b30]",
}[s] || "border-white/20 text-zinc-400");

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submitReview = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${reviewing.id}/review`, { rating, comment });
      toast.success("Thanks — your review is live");
      setReviewing(null); setComment(""); setRating(5);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const cancelBooking = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${cancelling.id}/cancel`, {});
      toast.success(data.refunded > 0 ? `Cancelled — $${data.refunded.toFixed(2)} refunded to your card` : "Cancelled");
      setCancelling(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="my-bookings-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
      <span className="text-[11px] uppercase tracking-[0.3em] text-[#00e5ff]">Your account</span>
      <h1 className="display text-4xl sm:text-5xl uppercase tracking-tighter font-bold mt-3">My bookings</h1>

      {bookings.length === 0 ? (
        <div data-testid="no-bookings" className="mt-12 border border-white/10 p-12 text-center">
          <p className="text-sm text-zinc-400">No sessions booked yet.</p>
          <Link to="/coaches" className="inline-block mt-6 bg-[#00e5ff] text-black text-xs font-bold uppercase px-6 py-3 hover:bg-[#00b3cc] transition-colors duration-200">Browse coaches</Link>
        </div>
      ) : (
        <div className="mt-12 space-y-3">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-row-${b.id}`} className="border border-white/10 bg-[#121212] p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center hover:border-white/25 transition-colors duration-200">
              <div className="md:col-span-4 flex items-center gap-4">
                {b.coach_photo && <img src={b.coach_photo} alt={b.coach_name} className="w-12 h-12 object-cover border border-white/10" />}
                <div>
                  <p className="display uppercase font-bold text-lg leading-none">{b.coach_name}</p>
                  <p className="text-[11px] text-zinc-500 mt-1">{b.service_title}</p>
                </div>
              </div>
              <div className="md:col-span-3 text-xs text-zinc-300 flex items-center gap-2">
                {b.kind === "video" || b.kind === "remote" ? <><Video className="w-3.5 h-3.5 text-[#00e5ff]" /> Remote</> : <><CalendarClock className="w-3.5 h-3.5 text-[#00e5ff]" /> {slotLabel(b.slot)}</>}
              </div>
              <div className="md:col-span-2 text-sm font-semibold text-[#00e5ff]">
                {money(b.total_charged ?? b.amount)}
                {b.client_fee ? <span className="block text-[10px] text-zinc-500 font-normal">incl. {money(b.client_fee)} service fee</span> : null}
              </div>
              <div className="md:col-span-3 md:text-right">
                <span className={`text-[10px] uppercase tracking-widest border px-3 py-1.5 ${badge(b.status)}`}>{b.status.replace("_", " ")}</span>
                {b.coach_note && <p className="text-[11px] text-zinc-400 mt-2">Coach note: {b.coach_note}</p>}
                {b.status === "completed" && (
                  <button
                    data-testid={`leave-review-${b.id}`}
                    onClick={() => setReviewing(b)}
                    className="mt-2 block md:ml-auto text-[10px] uppercase tracking-widest border border-[#00e5ff] text-[#00e5ff] px-3 py-2 hover:bg-[#00e5ff] hover:text-black transition-colors duration-200"
                  >
                    Leave a review
                  </button>
                )}
                {b.status === "confirmed" && b.refund_quote?.refundable && (
                  <button
                    data-testid={`cancel-booking-${b.id}`}
                    onClick={() => setCancelling(b)}
                    className="mt-2 block md:ml-auto text-[10px] uppercase tracking-widest border border-white/15 text-zinc-400 px-3 py-2 hover:border-[#ff3b30] hover:text-[#ff3b30] transition-colors duration-200"
                  >
                    Cancel session
                  </button>
                )}
                {b.status === "cancelled" && b.refund_amount > 0 && (
                  <p data-testid={`refund-note-${b.id}`} className="text-[11px] text-zinc-400 mt-2">{money(b.refund_amount)} refunded</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {cancelling && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-6">
          <div data-testid="cancel-modal" className="w-full max-w-md bg-[#121212] border border-white/15 p-8">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">Cancel session</span>
                <h2 className="display text-2xl uppercase tracking-tight font-bold">{cancelling.coach_name}</h2>
              </div>
              <button data-testid="cancel-close-btn" onClick={() => setCancelling(null)} className="text-zinc-500 hover:text-[#ff3b30] transition-colors duration-200"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-zinc-300 mt-5">{cancelling.service_title}</p>
            <p className="text-[11px] text-zinc-500 mt-1">{cancelling.slot ? slotLabel(cancelling.slot) : "Remote"}</p>
            <div className="mt-6 border border-white/10 p-4">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Refund</p>
              <p data-testid="cancel-refund-amount" className="display text-3xl font-bold text-[#00e5ff] leading-none mt-2">{money(cancelling.refund_quote?.amount)}</p>
              <p data-testid="cancel-policy" className="text-[11px] text-zinc-400 mt-3 leading-relaxed">{cancelling.refund_quote?.policy}</p>
            </div>
            <button
              data-testid="cancel-confirm-btn"
              disabled={busy}
              onClick={cancelBooking}
              className="mt-6 w-full bg-[#ff3b30] text-white font-bold uppercase tracking-wide py-3.5 hover:bg-[#cc2f26] disabled:opacity-50 transition-colors duration-200"
            >
              Cancel and refund
            </button>
          </div>
        </div>
      )}

      {reviewing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-6">
          <div data-testid="review-modal" className="w-full max-w-md bg-[#121212] border border-white/15 p-8">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">Review</span>
                <h2 className="display text-2xl uppercase tracking-tight font-bold">{reviewing.coach_name}</h2>
              </div>
              <button data-testid="review-close-btn" onClick={() => setReviewing(null)} className="text-zinc-500 hover:text-[#ff3b30] transition-colors duration-200"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex items-center gap-2 mt-6">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`review-star-${n}`} onClick={() => setRating(n)}>
                  <Star className={`w-7 h-7 transition-colors duration-200 ${n <= rating ? "text-[#00e5ff] fill-[#00e5ff]" : "text-zinc-700"}`} />
                </button>
              ))}
            </div>
            <textarea
              data-testid="review-comment-input"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What did the session cover? How was the coaching?"
              className="mt-5 w-full bg-black border border-white/10 text-sm px-3 py-2.5 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff] transition-colors duration-200"
            />
            <button
              data-testid="review-submit-btn"
              disabled={busy}
              onClick={submitReview}
              className="mt-5 w-full bg-[#00e5ff] text-black font-bold uppercase tracking-wide py-3.5 hover:bg-[#00b3cc] disabled:opacity-50 transition-colors duration-200"
            >
              Publish review
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
